import {
  PURCHASE_ORDER_STATUSES,
  PURCHASE_ORDER_STATUS_LABELS,
  centsToUnits,
  type PurchaseOrder,
  type PurchaseOrderStatus,
} from "@xop-any-time/shared";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState, type JSX } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useAuth } from "../../../auth/AuthProvider.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { ApiError } from "../../../lib/api.js";
import { dateOnly, money } from "../../../lib/format.js";
import { listPurchaseOrders, purchaseOrderKeys } from "../../../lib/purchaseOrdersApi.js";
import { CrmTable, PageHeader, type Column } from "../crm.js";
import { Pager } from "../Pager.js";
import {
  PURCHASE_ORDER_EXPLAINER,
  PurchaseOrderSourceChip,
  PurchaseOrderStatusChip,
  purchaseOrderTitle,
} from "./purchase-order-ui.js";

/**
 * Purchase orders — this reseller's orders TO XOP Networks (2026-09-17).
 *
 * The one money section that points the other way: invoices and quotes come
 * from XOP Networks, a purchase order goes to it. Rows are crm-api's, scoped to
 * the company's own subtree, and the STATUS is XOP's to move (received →
 * accepted or cancelled) — nothing on this page changes it.
 *
 * SERVER-SIDE filter and paging, unlike Quotes. A company's order history
 * grows without bound and crm-api pages it; the per-status `counts` on the
 * same answer label the filter, so choosing "Accepted" never has to guess how
 * many there are.
 */

type Filter = PurchaseOrderStatus | "all";

export function PurchaseOrdersPage(): JSX.Element {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [filter, setFilter] = useState<Filter>("all");
  const [page, setPage] = useState(1);

  const params = { page, status: filter };
  const { data, isPending, error } = useQuery({
    queryKey: purchaseOrderKeys.list(params),
    queryFn: () => listPurchaseOrders(params),
    placeholderData: keepPreviousData,
  });

  const rows = data?.purchaseOrders ?? [];
  const counts = data?.counts;
  const total = counts ? counts.received + counts.accepted + counts.cancelled : null;

  const columns: ReadonlyArray<Column<PurchaseOrder>> = [
    {
      key: "number",
      header: "PO",
      render: (po) => (
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontFamily: "monospace" }}>
            {purchaseOrderTitle(po)}
          </Typography>
          {/* The BUYER's own number under XOP's — the one the reseller will
              recognise from its own books. */}
          {po.poNumber && (
            <Typography data-testid="po-your-number" sx={{ fontSize: 11.5, color: "text.secondary" }}>
              Your PO # {po.poNumber}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      render: (po) =>
        po.customer?.companyName ? (
          <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{po.customer.companyName}</Typography>
        ) : (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>—</Typography>
        ),
    },
    { key: "orderDate", header: "Order date", render: (po) => dateOnly(po.orderDate) },
    {
      key: "total",
      header: "Total",
      align: "right",
      // An uploaded PO nobody itemised has no total, and says so with a dash
      // rather than a confident $0.00.
      render: (po) => money(centsToUnits(po.totalCents), po.currency),
    },
    { key: "source", header: "Source", render: (po) => <PurchaseOrderSourceChip source={po.source} /> },
    {
      key: "status",
      header: "Status",
      align: "right",
      render: (po) => <PurchaseOrderStatusChip status={po.status} />,
    },
  ];

  // The passthrough relays crm-api's refusal as a plain 403 — no permission
  // key rides along — so the page names the key it needs itself.
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Purchase orders" description={DESCRIPTION} />
        <NoAccessPanel permission="invoices:view" />
      </Box>
    );
  }

  const label = (value: Filter): string => {
    const name = value === "all" ? "All" : PURCHASE_ORDER_STATUS_LABELS[value];
    const n = value === "all" ? total : counts?.[value];
    return typeof n === "number" ? `${name} (${n})` : name;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Purchase orders"
        description={DESCRIPTION}
        actions={
          // A COURTESY, not a gate: crm-api refuses the POST without
          // `invoices:edit` regardless.
          can("invoices:edit") ? (
            <Button
              component={RouterLink}
              to="/purchase-orders/new"
              variant="contained"
              startIcon={<Plus size={16} />}
            >
              Add purchase order
            </Button>
          ) : null
        }
      />

      <Box>
        <CrmTable
          columns={columns}
          rows={rows}
          loading={isPending}
          error={error ? (error as Error).message : null}
          empty={
            filter === "all"
              ? `No purchase orders yet. ${PURCHASE_ORDER_EXPLAINER}`
              : `No ${PURCHASE_ORDER_STATUS_LABELS[filter].toLowerCase()} purchase orders.`
          }
          rowKey={(po) => po.id}
          onRowClick={(po) => navigate(`/purchase-orders/${po.id}`)}
          toolbar={
            <TextField
              select
              size="small"
              label="Filter by status"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value as Filter);
                setPage(1);
              }}
              sx={{ minWidth: 200 }}
            >
              {(["all", ...PURCHASE_ORDER_STATUSES] as const).map((value) => (
                <MenuItem key={value} value={value}>
                  {label(value)}
                </MenuItem>
              ))}
            </TextField>
          }
        />
        <Pager pagination={data?.pagination} onPage={setPage} />
      </Box>
    </Box>
  );
}

const DESCRIPTION =
  "Your orders to XOP Networks — made from a quote you accepted, or uploaded from your own purchasing system.";

export default PurchaseOrdersPage;
